import { zplTextBreite, qrModuleFuerDaten } from '../services/printerService';

/**
 * 👁️ Etikett-Vorschau: zeichnet die ZPL-Zeilen, die wirklich an den Drucker gehen würden, als SVG.
 * Versteht genau die Befehle, die printerService erzeugt (^PW ^LL ^FO ^A0N ^FB ^FD ^GB) — Breiten
 * kommen aus derselben Zeichentabelle wie der Druck, die Vorschau bricht also um wie der Drucker.
 */
function umbruch(text, h, breite) {
  const leer = zplTextBreite(' ', h);
  const out = [];
  let zeile = '', lauf = 0;
  for (const wort of String(text || '').split(' ')) {
    const b = zplTextBreite(wort, h);
    const neu = zeile ? lauf + leer + b : b;
    if (zeile && neu > breite) { out.push(zeile); zeile = wort; lauf = b; } else { zeile = zeile ? `${zeile} ${wort}` : wort; lauf = neu; }
  }
  out.push(zeile);
  return out;
}

export function zplZuFormen(lines) {
  const z = (lines || []).join('\n');
  const rawW = +(/\^PW(\d+)/.exec(z) || [0, 812])[1];
  const rawH = +(/\^LL(\d+)/.exec(z) || [0, 1218])[1];
  const istRotiert90 = /\^A0R,/.test(z) && !/\^A0N,/.test(z);
  const w = istRotiert90 ? rawH : rawW;
  const h = istRotiert90 ? rawW : rawH;
  const formen = [];
  for (const m of z.matchAll(/\^FO(\d+),(\d+)\^GB(\d+),(\d+),(\d+)\^FS/g)) {
    const [rx, ry, rbw, rbh, t] = m.slice(1).map(Number);
    const x = istRotiert90 ? ry : rx;
    const y = istRotiert90 ? rawW - rx - rbw : ry;
    const bw = istRotiert90 ? rbh : rbw;
    const bh = istRotiert90 ? rbw : rbh;
    if (t * 2 >= Math.min(bw, bh)) formen.push({ art: 'flaeche', x, y, w: bw, h: bh });
    else formen.push({ art: 'rahmen', x, y, w: bw, h: bh, t });
  }
  for (const m of z.matchAll(/\^FO(\d+),(\d+)\^A0N,(\d+),(\d+)(?:\^FB(\d+),(\d+),0,([CLR]))?\^FD([^^]*)\^FS/g)) {
    const x = +m[1], y = +m[2], sh = +m[3];
    const fb = m[5] ? +m[5] : null;
    const zeilen = fb ? umbruch(m[8], sh, fb).slice(0, +m[6]) : [m[8]];
    zeilen.forEach((text, i) => {
      const tw = zplTextBreite(text, sh);
      let tx = x;
      if (fb && m[7] === 'C') tx = x + (fb - tw) / 2;
      if (fb && m[7] === 'R') tx = x + fb - tw;
      formen.push({ art: 'text', x: tx, y: y + i * sh, h: sh, w: tw, text });
    });
  }
  for (const m of z.matchAll(/\^FO(\d+),(\d+)\^A0R,(\d+),(\d+)\^FD([^^]*)\^FS/g)) {
    const rx = +m[1], ry = +m[2], sh = +m[3], text = m[5];
    const tw = zplTextBreite(text, sh);
    formen.push({ art: 'text', x: ry, y: rawW - rx - sh, h: sh, w: tw, text });
  }
  // Barcode / QR-Code: Lage und Größe wie im Druck; das Muster ist nur angedeutet (der Drucker erzeugt den echten Code)
  for (const m of z.matchAll(/\^FO(\d+),(\d+)\^BY(\d+),2(?:\.0)?,(\d+)\^BCN,(\d+),N,N,N\^FD([^^]*)\^FS/g)) {
    const [x, y, modul, , bh] = m.slice(1, 6).map(Number);
    const daten = m[6];
    const module = (/^\d+$/.test(daten) ? Math.ceil(daten.length / 2) : daten.length) * 11 + 35;
    formen.push({ art: 'barcode', x, y, w: module * modul, h: bh, daten });
  }
  for (const m of z.matchAll(/\^FO(\d+),(\d+)\^BY(\d+),2(?:\.0)?,(\d+)\^BCR,(\d+),N,N,N\^FD([^^]*)\^FS/g)) {
    const [rx, ry, modul, , bh] = m.slice(1, 6).map(Number);
    const daten = m[6];
    const module = (/^\d+$/.test(daten) ? Math.ceil(daten.length / 2) : daten.length) * 11 + 35;
    formen.push({ art: 'barcode', x: ry, y: rawW - rx - bh, w: module * modul, h: bh, daten });
  }
  for (const m of z.matchAll(/\^FO(\d+),(\d+)\^BQN,2,(\d+)\^FDQA,([^^]*)\^FS/g)) {
    const [x, y, mag] = m.slice(1, 4).map(Number);
    formen.push({ art: 'qr', x, y, s: qrModuleFuerDaten(m[4]) * mag, daten: m[4] });
  }
  for (const m of z.matchAll(/\^FO(\d+),(\d+)\^GFA,\d+,\d+,(\d+),([0-9A-F]+)\^FS/g)) {
    const x = +m[1], y = +m[2], bpr = +m[3], hex = m[4];
    const gh = bpr > 0 ? Math.floor(hex.length / (bpr * 2)) : 0;
    if (bpr > 0 && gh > 0) formen.push({ art: 'gfa', x, y, w: bpr * 8, h: gh, bpr, hex });
  }
  return { w, h, formen };
}

// Angedeutetes Strich-/Punktmuster, abgeleitet aus den Daten (gleiche Daten → gleiches Muster)
function mischer(daten) {
  let h = 2166136261;
  for (const ch of String(daten)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return () => { h ^= h << 13; h >>>= 0; h ^= h >>> 17; h ^= h << 5; h >>>= 0; return h; };
}
function BarcodeSkizze({ f }) {
  const r = mischer(f.daten);
  const balken = [];
  let x = 0;
  while (x < f.w) { const b = 1 + (r() % 3), l = 1 + (r() % 3); balken.push([x, b]); x += b + l; }
  const k = f.w / Math.max(1, x);
  return <g data-testid="label-preview-barcode">{balken.map(([bx, b], i) => <rect key={i} x={f.x + bx * k} y={f.y} width={Math.max(1, b * k)} height={f.h} fill="#000" />)}</g>;
}
function QrSkizze({ f }) {
  const n = 21, z = f.s / n, r = mischer(f.daten);
  const sucher = (cx, cy) => (cx < 7 && cy < 7) || (cx >= n - 7 && cy < 7) || (cx < 7 && cy >= n - 7);
  const zellen = [];
  for (let cy = 0; cy < n; cy++) for (let cx = 0; cx < n; cx++) {
    let an;
    if (sucher(cx, cy)) { const lx = cx < 7 ? cx : cx - (n - 7), ly = cy < 7 ? cy : cy - (n - 7); an = lx === 0 || lx === 6 || ly === 0 || ly === 6 || (lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4); }
    else an = r() % 2 === 0;
    if (an) zellen.push([cx, cy]);
  }
  return <g data-testid="label-preview-qr">{zellen.map(([cx, cy], i) => <rect key={i} x={f.x + cx * z} y={f.y + cy * z} width={z} height={z} fill="#000" />)}</g>;
}
function GfaSkizze({ f }) {
  const pfade = [];
  for (let ry = 0; ry < f.h; ry++) {
    let start = -1;
    const off = ry * f.bpr * 2;
    for (let b = 0; b < f.bpr; b++) {
      const byte = parseInt(f.hex.substr(off + b * 2, 2), 16) || 0;
      if (byte === 0) {
        if (start >= 0) { pfade.push(`M${f.x + start},${f.y + ry + 0.5}h${b * 8 - start}`); start = -1; }
        continue;
      }
      if (byte === 255) {
        if (start < 0) start = b * 8;
        continue;
      }
      for (let bit = 0; bit < 8; bit++) {
        const an = (byte & (0x80 >> bit)) !== 0;
        const px = b * 8 + bit;
        if (an && start < 0) start = px;
        else if (!an && start >= 0) { pfade.push(`M${f.x + start},${f.y + ry + 0.5}h${px - start}`); start = -1; }
      }
    }
    if (start >= 0) pfade.push(`M${f.x + start},${f.y + ry + 0.5}h${f.bpr * 8 - start}`);
  }
  return <path data-testid="label-preview-gfa" d={pfade.join('')} stroke="#000" strokeWidth={1.15} fill="none" />;
}

export default function LabelPreview({ lines, mm, maxBreite = 240 }) {
  const { w, h, formen } = zplZuFormen(lines);
  const breite = Math.min(maxBreite, Math.max(90, Math.round(maxBreite * (w / 812))));
  return (
    <div data-testid="label-preview" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg viewBox={`0 0 ${w} ${h}`} width={breite} style={{ background: '#fff', border: '1px solid #bbb', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.18)', display: 'block', maxHeight: 300 }}
        preserveAspectRatio="xMidYMin meet">
        {formen.map((f, i) => {
          if (f.art === 'flaeche') return <rect key={i} x={f.x} y={f.y} width={f.w} height={f.h} fill="#000" />;
          if (f.art === 'barcode') return <BarcodeSkizze key={i} f={f} />;
          if (f.art === 'qr') return <QrSkizze key={i} f={f} />;
          if (f.art === 'gfa') return <GfaSkizze key={i} f={f} />;
          if (f.art === 'rahmen') return <rect key={i} x={f.x + f.t / 2} y={f.y + f.t / 2} width={Math.max(0, f.w - f.t)} height={Math.max(0, f.h - f.t)} fill="none" stroke="#000" strokeWidth={f.t} />;
          return (
            <text key={i} x={f.x} y={f.y + f.h * 0.78} fontSize={f.h * 0.98} fontFamily="'Arial Narrow', 'Roboto Condensed', Arial, sans-serif" fontWeight="700"
              textLength={Math.max(1, f.w)} lengthAdjust="spacingAndGlyphs" fill="#000">{f.text}</text>
          );
        })}
      </svg>
      {mm && <div style={{ fontSize: 11, color: '#888' }}>{mm}</div>}
    </div>
  );
}
